/**
 * クラス名：iq3装置設定の曲げ長さ係数画面
 * 作成者：チュー
 * 作成日：2023/06/01
 * バージョン：1.0
 */

import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { Row, Col, Input, Button, Space, Popover } from 'antd';
import { PlusCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { Table } from 'react-bootstrap';
import { isEmpty } from '@rc-component/mini-decimal/es/numberUtil';

import { commonModal } from '../../../common/CommonModal';
import {
  deleteListColData,
  updateListColData,
  sortListData,
  getListItemByCustomData,
  getInputBorderStyle,
  handleKeyPress,
  regexExp,
  toHalfWidth,
  isDigitZenkaku,
} from '../../../common/Common';
import { WorkType } from '../../../common/enums';

const Device_BendingTimeFactor = forwardRef((props, ref) => {

  const [editMode, setEditMode] = useState(false);

  const [isLength, setIsLength] = useState(false);
  const [isWeight, setIsWeight] = useState(false);
  const [category, setCategory] = useState('');

  const [weightData, setWeightData] = useState([]);
  const [lengthData, setLengthData] = useState([]);
  const [detailsLengthTemp, setDetailsLengthTemp] = useState([]);
  const [detailsWeightTemp, setDetailsWeightTemp] = useState([]);
  // 曲げ長さ係数リスト
  const [detailsList, setDetailsList] = useState([]);

  const [errIndexVal, setErrIndexVal] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const inputRef = useRef([]);

  useEffect(() => {
    setErrIndexVal([]);
    setErrorMessage('');
    setEditMode(props.editMode);
    
    // 曲げ長さ係数
    if (props?.detailsData?.bendTimeCoeffWeightThMaster) {
      setWeightData(props?.detailsData?.bendTimeCoeffWeightThMaster);
      setDetailsWeightTemp(props?.detailsData?.bendTimeCoeffWeightThMaster);
    }
    if (props?.detailsData?.bendTimeCoeffLenThMaster) {
      setLengthData(props?.detailsData?.bendTimeCoeffLenThMaster);
      setDetailsLengthTemp(props?.detailsData?.bendTimeCoeffLenThMaster);
    }
    if (props?.detailsData?.bendTimeCoeffWeightLenItems) {
      setDetailsList(props?.detailsData?.bendTimeCoeffWeightLenItems);
    }

    if (props?.detailsData?.mtWeightThMaster) {
      setWeightData(props?.detailsData?.mtWeightThMaster);
      setDetailsWeightTemp(props?.detailsData?.mtWeightThMaster);
    }
    setCategory(props.processName);
  }, [
    props.detailsData,
    props.seisanKosuuList,
    props.editMode,
    props.processName,
    props.workCorrectionTimeListData,
    props.iq3BendingKosuuList,
  ]);

  const addData = () => {
    setIsUpdateData(true);
  };

  const cancelMasterData = () => {
    if (isWeight === true) {
      setErrIndexVal([]);
      setErrorMessage('');
      setDetailsWeightTemp(weightData);
      setIsWeight(false);
    }
  };

  const isValueOrderCorrect = (e, no, field, tblName) => {
    let temp = [];
    if (tblName === 'length') {
      temp = detailsLengthTemp?.filter((i) => i.isOutOfRange === false);
    } else if (tblName === 'weight') {
      temp = detailsWeightTemp?.filter((i) => i.isOutOfRange === false);
    } 
    const insertIndex = temp?.findIndex((item) => item.no === no);
    if (regexExp.test(temp[insertIndex].value) && !isDigitZenkaku(temp[insertIndex].value)) return;
    // IQX_WEBEST-220の対応-START
    if (regexExp.test(temp[insertIndex].value)) {
      // 全角の場合、
      temp[insertIndex].value = toHalfWidth(temp[insertIndex].value); // 半角へ変換
      e.target.value = temp[insertIndex].value;
      if (tblName === 'length') handleLengthChange(e, no, field);
      else if (tblName === 'weight') handleWeightChange(e, no, field);
    }
    if (isNaN(parseFloat(temp[insertIndex].value))) {
      setErrIndexVal([...errIndexVal, insertIndex]);
      setErrorMessage('数字値を入力してください！');
      return;
    }
    // IQX_WEBEST-220の対応-END

    if (insertIndex > 0) {
      const prevValue = parseFloat(temp[insertIndex - 1].value);
      const currentValue = parseFloat(temp[insertIndex].value);
      const nextValue = parseFloat(temp[insertIndex + 1].value);
      if (currentValue > prevValue && currentValue < nextValue) {
        let temp = [insertIndex - 1, insertIndex, insertIndex + 1];

        setErrIndexVal(errIndexVal?.filter((item) => item !== insertIndex && item != insertIndex - 1));

        setErrorMessage('前のしきい値より大きい値を入力してください！');
      } else {
        setErrIndexVal([...errIndexVal, insertIndex]);

        if (currentValue <= prevValue) {
          setErrorMessage('前のしきい値より大きい値を入力してください！');
        } else if (currentValue >= nextValue) {
          setErrorMessage('次のしきい値より小さい値を入力してください！');
        }
      }
    } else {
      const currentValue = parseFloat(temp[insertIndex].value);
      const nextValue = parseFloat(temp[insertIndex + 1].value);

      if (currentValue < nextValue) {
        setErrIndexVal(errIndexVal?.filter((item) => item !== insertIndex));
      } else {
        setErrIndexVal([...errIndexVal, insertIndex]);
        setErrorMessage('次のしきい値より小さい値を入力してください！');
      }
    }
  };

  const addLength = () => {
    setIsLength(true);
  };

  const addWeight = () => {
    setIsWeight(true);
  };

  const addLengthModalTitle = (
    <div
      style={{
        width: 400,
        backgroundColor: '#005fab',
        marginLeft: -24,
        height: 36,
        marginTop: -20,
        borderTopLeftRadius: 7,
        borderTopRightRadius: 7,
        fontSize: 14,
      }}
    >
      <p style={{ paddingTop: 8, paddingLeft: 18, color: 'white' }}>mm</p>
    </div>
  );

  const addWeightModalTitle = (
    <div
      style={{
        width: 400,
        backgroundColor: '#005fab',
        marginLeft: -24,
        height: 36,
        marginTop: -20,
        borderTopLeftRadius: 7,
        borderTopRightRadius: 7,
        fontSize: 14,
      }}
    >
      <p style={{ paddingTop: 8, paddingLeft: 18, color: 'white' }}>重量</p>
    </div>
  );

  const addLengthRow = (id, index) => {
    inputRef.current[index].focus();
    const copyData = detailsLengthTemp.slice(); // Create a copy of the original array

    const insertIndex = detailsLengthTemp?.findIndex((item) => item.no === id); // Find the index of the item with id 2

    if (insertIndex !== -1) {
      const data = {
        no: detailsLengthTemp?.length + 1,
        value: 0,
        isOutOfRange: false,
      };
      copyData.splice(insertIndex + 1, 0, data); // Insert the new object after the found index
      setDetailsLengthTemp(copyData);
    }
  };

  const deleteLength = (id, e, index) => {
    // 更新するエラーインデックスを得る
    const updatedErrorIndexes = errIndexVal?.filter((item) => item !== index);

    const finalErrorIndexes = updatedErrorIndexes?.map((item) => (item > index ? item - 1 : item));
    setErrIndexVal(finalErrorIndexes);

    if (detailsLengthTemp?.filter((item) => item.isOutOfRange === false)?.length > 2) {
      const updatedData = detailsLengthTemp?.map((row) => {
        if (row.no === id) {
          return { ...row, isOutOfRange: true };
        }
        return row;
      });
      setDetailsLengthTemp(updatedData);
      const filterData = updatedData?.filter((i) => !i.isOutOfRange);
      if (index + 1 !== filterData?.length) {
        inputRef.current[index].focus();
      }
    }
  };
  const deleteWeight = (id, e, index) => {
    // 更新するエラーインデックスを得る
    const updatedErrorIndexes = errIndexVal?.filter((item) => item !== index);

    const finalErrorIndexes = updatedErrorIndexes?.map((item) => (item > index ? item - 1 : item));
    setErrIndexVal(finalErrorIndexes);

    if (detailsWeightTemp?.filter((item) => item.isOutOfRange === false)?.length > 2) {
      const updatedData = detailsWeightTemp?.map((row) => {
        if (row.no === id) {
          return { ...row, isOutOfRange: true };
        }
        return row;
      });
      setDetailsWeightTemp(updatedData);
      const filterData = updatedData?.filter((i) => !i.isOutOfRange);
      if (index + 1 !== filterData?.length) {
        inputRef.current[index].focus();
      }
    }
  };
  const addWeightRow = (id, index) => {
    inputRef.current[index].focus();
    const copyData = detailsWeightTemp.slice(); // Create a copy of the original array

    const insertIndex = detailsWeightTemp?.findIndex((item) => item.no === id); // Find the index of the item with id 2

    if (insertIndex !== -1) {
      const data = {
        no: detailsWeightTemp?.length + 1,
        value: 0,
        isOutOfRange: false,
      };
      copyData.splice(insertIndex + 1, 0, data); // Insert the new object after the found index
      setDetailsWeightTemp(copyData);
    }
  };
  const closeAddModal = () => {
    if (isLength === true) {
      setIsLength(false);
    } else if (isWeight === true) {
      setIsWeight(false);
    }
  };
  const handleLengthChange = (event, id, field) => {
    if (regexExp.test(event.target.value) && !isDigitZenkaku(event.target.value)) return;
    const updatedData = detailsLengthTemp?.map((row) => {
      if (row.no === id) {
        return { ...row, [field]: event.target.value };
      }
      return row;
    });
    setDetailsLengthTemp(updatedData);
  };
  const handleWeightChange = (event, id, field) => {
    if (regexExp.test(event.target.value) && !isDigitZenkaku(event.target.value)) return;
    const updatedData = detailsWeightTemp?.map((row) => {
      if (row.no === id) {
        return { ...row, [field]: event.target.value };
      }
      return row;
    });
    setDetailsWeightTemp(updatedData);
  };

  const cancelLengthMaster = () => {
    setDetailsLengthTemp(lengthData);
    setIsLength(false);
  };
  
  const addLengthMaster = () => {
    const deletedId = [];

    lengthData?.forEach((item1, index) => {
      const found = detailsLengthTemp?.find((item2) => item2.no === item1.no && item2.isOutOfRange === true);
      if (found) {
        deletedId.push(item1.no);
      }
    });

    const newId = detailsLengthTemp
      ?.filter((item) => !item.isOutOfRange && !lengthData.some((data2Item) => data2Item.no === item.no))
      ?.map((item) => item.no);

    const deleteData = detailsList?.map((item) => {
      return {
        ...item,
        lengthCoeffList: deleteListColData(item?.lengthCoeffList, deletedId),
      };
    });

    const updatedData = deleteData?.map((item) => {
      newId?.forEach((key) => {
        // Add new objects to lstData
        item?.lengthCoeffList.push({
          no: key,
          value: 1,
          isOutOfRange: false,
        });
      });
      return item;
    });

    const sortedList = updatedData?.map((item) => {
      const sortLstData = [...item?.lengthCoeffList];
      const sortedLstData = sortListData(sortLstData, detailsLengthTemp);
      return {
        ...item,
        lengthCoeffList: sortedLstData,
      };
    });
    setIsLength(false);
    setLengthData(detailsLengthTemp);

    setDetailsList(sortedList);
    props.updatedLength(detailsLengthTemp);

    props.updatedSizesList(sortedList);
  };

  const addWeightMaster = () => {
    const deletedId = [];

    weightData?.forEach((item1, index) => {
      const found = detailsWeightTemp?.find((item2) => item2.no === item1.no && item2.isOutOfRange === true);
      if (found) {
        deletedId.push(item1.no);
      }
    });

    const newId = detailsWeightTemp
      ?.filter((item) => !item.isOutOfRange && !weightData.some((data2Item) => data2Item.no === item.no))
      ?.map((item) => item.no);

    setIsWeight(false);
    setWeightData(detailsWeightTemp);

    if (category === WorkType.SmBending) {
      props.updatedWeight(detailsWeightTemp);
      const customItem = { weightId: 0, lengthCoeffList: getListItemByCustomData(lengthData) };

      const deleteData = deleteListColData(detailsList, deletedId, false, 'weightId');
      const updatedData = updateListColData(deleteData, newId, false, { ...customItem }, 'weightId');
      const result = sortListData(updatedData, detailsWeightTemp, false, 'weightId');

      // const updatedList = updateSizeList(detailsList, deletedId, newId);
      setDetailsList(result);
      props.updatedSizesList(result);
    } 
  };
  
  const addLengthContent = (
    <>
      <div style={{ height: 400, overflow: 'auto' }}>
        <Row className="chargeAmount registerModal">
          <Col style={{ width: '100%' }}>
            <Table className="master-table">
              <thead>
                <tr style={{ textAlign: 'center' }}>
                  <th style={{ width: '20%' }} className="param-tbl-header">
                    No
                  </th>

                  <th style={{ width: '60%' }} className="param-tbl-header">
                    <Space>
                      <label>しきい値</label>（≦）
                    </Space>
                  </th>
                </tr>
              </thead>
              <tbody>
                {detailsLengthTemp
                  ?.filter((subitem) => subitem.isOutOfRange === false)
                  ?.map((item, index) => (
                    <tr key={index} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      <td>{index + 1}</td>

                      <td>
                        {errIndexVal?.includes(index) ? (
                          <Popover
                            content={<div style={{ padding: 7 }}>{errorMessage}</div>}
                            placement="left"
                            visible={true}
                          ></Popover>
                        ) : (
                          ''
                        )}
                        <Input
                          value={item.value}
                          onChange={(e) => handleLengthChange(e, item.no, 'value')}
                          onBlur={(e) => isValueOrderCorrect(e, item.no, 'value', 'length')}
                          onPressEnter={(e) => isValueOrderCorrect(e, item.no, 'value', 'length')}
                          className={errIndexVal?.includes(index) ? 'red-border' : ''}
                          style={{
                            ...getInputBorderStyle(index, detailsLengthTemp),
                            pointerEvents: editMode ? 'auto' : 'none',
                          }}
                          onKeyPress={(e) => {
                            handleKeyPress(e, false);
                          }}
                          ref={(ref) => (inputRef.current[index] = ref)}
                        ></Input>
                      </td>
                      <td
                        style={{ border: 'none', backgroundColor: 'white', verticalAlign: 'middle', width: '20%' }}
                        className="min"
                      >
                        <Row justify="center">
                          <PlusCircleFilled
                            onClick={() => addLengthRow(item.no, index + 1)}
                            className="add-remove-icon"
                            style={{ marginLeft: 5, marginRight: 5, pointerEvents: editMode ? 'auto' : 'none' }}
                          />
                          <CloseCircleFilled
                            className="add-remove-icon"
                            onClick={(e) => deleteLength(item.no, e, index)}
                            style={{ pointerEvents: editMode ? 'auto' : 'none' }}
                          />
                        </Row>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      </div>
      <Row justify="center" className="mt-2">
        <Button
          className={isEmpty(errIndexVal) ? 'mainButton' : 'disabledButton'}
          id="update"
          onClick={addLengthMaster}
        >
          OK
        </Button>
        <Button className="cancelButton" style={{ marginLeft: 10 }} onClick={cancelLengthMaster}>
          キャンセル
        </Button>
      </Row>
    </>
  );

  const addWeightContent = (
    <>
      <div style={{ height: 400, overflow: 'auto' }}>
        <Row className="chargeAmount registerModal">
          <Col style={{ width: '100%' }}>
            <Table className="master-table">
              <thead>
                <tr style={{ textAlign: 'center' }}>
                  <th style={{ width: '20%' }} className="param-tbl-header">
                    No
                  </th>

                  <th style={{ width: '60%' }} className="param-tbl-header">
                    <Space>
                      <label>しきい値</label>（≦）
                    </Space>
                  </th>
                </tr>
              </thead>
              <tbody>
                {detailsWeightTemp
                  ?.filter((subitem) => subitem.isOutOfRange === false)
                  ?.map((item, index) => (
                    <tr key={index} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                      <td>{index + 1}</td>

                      <td>
                        {errIndexVal?.includes(index) ? (
                          <Popover
                            content={<div style={{ padding: 7 }}>{errorMessage}</div>}
                            placement="left"
                            visible={true}
                          ></Popover>
                        ) : (
                          ''
                        )}
                        <Input
                          value={item.value}
                          onChange={(e) => handleWeightChange(e, item.no, 'value')}
                          onBlur={(e) => isValueOrderCorrect(e, item.no, 'value', 'weight')}
                          onPressEnter={(e) => isValueOrderCorrect(e, item.no, 'value', 'weight')}
                          className={errIndexVal?.includes(index) ? 'red-border' : ''}
                          style={{
                            ...getInputBorderStyle(index, detailsWeightTemp),
                            pointerEvents: editMode ? 'auto' : 'none',
                          }}
                          onKeyPress={(e) => {
                            handleKeyPress(e, false);
                          }}
                          ref={(ref) => (inputRef.current[index] = ref)}
                        ></Input>
                      </td>
                      <td
                        style={{ border: 'none', backgroundColor: 'white', verticalAlign: 'middle', width: '20%' }}
                        className="min"
                      >
                        <Row justify="center">
                          <PlusCircleFilled
                            onClick={() => addWeightRow(item.no, index + 1)}
                            className="add-remove-icon"
                            style={{ marginLeft: 5, marginRight: 5, pointerEvents: editMode ? 'auto' : 'none' }}
                          />
                          <CloseCircleFilled
                            className="add-remove-icon"
                            onClick={(e) => deleteWeight(item.no, e, index)}
                            style={{ pointerEvents: editMode ? 'auto' : 'none' }}
                          />
                        </Row>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      </div>
      <Row justify="center" className="mt-2">
        <Button
          className={isEmpty(errIndexVal) ? 'mainButton' : 'disabledButton'}
          id="update"
          onClick={addWeightMaster}
        >
          OK
        </Button>
        <Button className="cancelButton" style={{ marginLeft: 10 }} onClick={cancelMasterData}>
          キャンセル
        </Button>
      </Row>
    </>
  );

  const listDataChange = (value, wId, no, tblName) => {
    if (regexExp.test(value) && !isDigitZenkaku(value)) return;
    if (tblName === 'magechou') {
      const copyData = [...detailsList];
      const updatedData = JSON.parse(JSON.stringify(copyData));
      updatedData?.map((list) => {
        if (list?.weightId === wId) {
          list?.lengthCoeffList?.map((data) => {
            if (data.no === no) {
              data.value = value;
            }
          });
        }
      });
      setDetailsList(updatedData);
      props.updatedSizesList(updatedData);
    } 
  };

  const listDataChangeOnBlur = (value, wId, no, tblName) => {
    if (regexExp.test(value) && !isDigitZenkaku(value)) return;
    if (tblName === 'magechou') {
      const copyData = [...detailsList];
      const updatedData = JSON.parse(JSON.stringify(copyData));
      updatedData?.map((list) => {
        if (list?.weightId === wId) {
          list?.lengthCoeffList?.map((data) => {
            if (data.no === no) {
              value = regexExp.test(value) ? toHalfWidth(value) : value;
              data.value = parseFloat(value) > 0 ? value : 1;
            }
          });
        }
      });
      setDetailsList(updatedData);
      props.updatedSizesList(updatedData);
    } 
  };

  return (
    <>
      <div style={{ marginLeft: 10, marginRight: 10 }}>
       
          <>
            <Row className="table-container">
              <Table
                style={{
                  width:
                    lengthData?.filter((subitem) => !subitem.isOutOfRange)?.length > 8
                      ? '100%'
                      : lengthData?.filter((subitem) => !subitem.isOutOfRange)?.length * 98 + 196 + 'px',
                }}
              >
                <tr style={{ textAlign: 'center' }}>
                  <td style={{ width: '196px' }} className="param-tbl-header fixedCol">
                    <Row justify="center" className=" ant-row-no-bg-color">
                      <Space>
                        {editMode ? (
                          <>
                            <Button className="cancleButton" onClick={addWeight} style={{ pointerEvents: 'auto' }}>
                              kg
                            </Button>
                            /
                            <Button className="cancleButton" onClick={addLength} style={{ pointerEvents: 'auto' }}>
                              mm
                            </Button>
                          </>
                        ) : (
                          <>
                            <label>kg / mm</label>
                          </>
                        )}
                      </Space>
                    </Row>
                  </td>

                  {lengthData
                    ?.filter((subitem) => !subitem.isOutOfRange)
                    ?.map((item, index) => (
                      <td key={index} style={{ width: '98px' }} className="param-tbl-header">
                        ～{item.value}mm
                      </td>
                    ))}
                </tr>

                {detailsList?.map((row, index) => (
                  <tr key={index} style={{ textAlign: 'center' }}>
                    <td style={{ width: '196px' }} className="param-tbl-header fixedCol">
                      {/* ～{row.max} */}
                      {weightData?.filter((wItem) => wItem.no === row?.weightId)?.[0]?.value}kg {/**IQX_WEBEST-269*/}
                    </td>

                    {row?.lengthCoeffList?.map((col, index) => (
                      <td key={index} style={{ width: '98px' }}>
                        <Input
                          value={col.value}
                          onChange={(e) => listDataChange(e.target.value, row?.weightId, col.no, 'magechou')}
                          onBlur={(e) => listDataChangeOnBlur(e.target.value, row?.weightId, col.no, 'magechou')}
                          onPressEnter={(e) => listDataChangeOnBlur(e.target.value, row?.weightId, col.no, 'magechou')}
                          style={{ padding: 3, width: '98.2%', height: 26, textAlign: 'center' }}
                          className={editMode ? 'input-editable ' : 'input-non-editable'}
                          onKeyPress={(e) => {
                            handleKeyPress(e, false);
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </Table>
            </Row>
          </>
        
      </div>

      {/* 曲長 */}
      {isLength
        ? commonModal(
            isLength,
            addLengthModalTitle,
            null,
            null,
            400,
            addData,
            closeAddModal,
            addLengthContent,
            null,
            null,
            true
          )
        : ''}
      {/* 重量 */}
      {isWeight
        ? commonModal(
            isWeight,
            addWeightModalTitle,
            null,
            null,
            400,
            addData,
            closeAddModal,
            addWeightContent,
            null,
            null,
            true
          )
        : ''}
    </>
  );
});

export default Device_BendingTimeFactor;
